import type { Catalog } from '../catalog';

const HERO_PREFIX = 'npc_dota_hero_';
const ITEM_PREFIX = 'item_';
const ABILITY_LUA_SUFFIX = '_lua';

export type DotaAssetKind = 'hero' | 'ability' | 'item' | 'generic';

export const toHeroSlug = (heroName?: string) => heroName?.replace(HERO_PREFIX, '') || '';

export const toItemSlug = (itemName?: string) => itemName?.replace(new RegExp(`^${ITEM_PREFIX}`), '') || '';

export const toAbilitySlug = (abilityName?: string) => abilityName?.replace(/(_(?:lua|ad))+$/i, '') || '';

export const toDotaDisplayName = (raw?: string) =>
  raw
    ?.replace(/^npc_dota_hero_/, '')
    .replace(/^item_/, '')
    .replace(/(_(?:lua|ad))+$/i, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, symbol => symbol.toUpperCase()) || 'Unknown';

export const getDotaDisplayName = (raw?: string, _kind: DotaAssetKind = 'generic') => toDotaDisplayName(raw);

// ─── Catalog override helpers ─────────────────────────────────────────────────

/**
 * Prefer an override value; fall back when the override is null, undefined,
 * or a blank string (defensive against empty DB rows).
 */
const pickOverride = <T,>(override: T | null | undefined, fallback: T): T => {
  if (typeof override === 'string') return ((override as string).trim() ? override : fallback) as T;
  return override ?? fallback;
};

/**
 * Catalog ability codes typically embed the owner hero slug as prefix
 * (`nevermore_shadowraze1`, `crystal_maiden_freezing_field`). Default fallback
 * label should drop that prefix so the user sees "Shadowraze1", not
 * "Nevermore Shadowraze1". The list of hero slugs comes from the catalog;
 * longer slugs are tried first so `crystal_maiden_*` wins over `crystal_*`.
 */
let cachedHeroesRef: Catalog['heroes'] | undefined;
let cachedHeroSlugs: string[] = [];
const getHeroSlugsCached = (catalog: Catalog): string[] => {
  if (cachedHeroesRef !== catalog.heroes) {
    cachedHeroesRef = catalog.heroes;
    cachedHeroSlugs = Object.keys(catalog.heroes)
      .map(toHeroSlug)
      .filter(slug => slug.length > 0)
      .sort((a, b) => b.length - a.length);
  }
  return cachedHeroSlugs;
};

const stripHeroSlugPrefix = (catalog: Catalog, abilityName: string): string => {
  const slugs = getHeroSlugsCached(catalog);
  for (const slug of slugs) {
    const prefix = `${slug}_`;
    if (abilityName.startsWith(prefix)) return abilityName.slice(prefix.length);
  }
  return abilityName;
};

export const resolveHeroDisplayName = (catalog: Catalog, heroName?: string): string =>
  pickOverride(catalog.heroes[heroName ?? '']?.displayName, toDotaDisplayName(heroName));

export const resolveAbilityDisplayName = (catalog: Catalog, abilityName?: string): string => {
  const override = catalog.abilities[abilityName ?? '']?.displayName;
  if (typeof override === 'string' && override.trim()) return override;
  if (!abilityName) return toDotaDisplayName(abilityName);
  return toDotaDisplayName(stripHeroSlugPrefix(catalog, abilityName));
};

export const resolveItemDisplayName = (catalog: Catalog, itemName?: string): string =>
  pickOverride(catalog.items[itemName ?? '']?.displayName, toDotaDisplayName(itemName));
